import { all, put, takeEvery } from 'redux-saga/effects';
import {
  change, doSubmit, startSubmit, stopSubmit, reset,
} from 'circe/forms/actions';
import Auth from '@aws-amplify/auth';
import Storage from '@aws-amplify/storage';
import vehicleDefaults from 'domain/vehicleDefaults.json';
import { exception } from 'circe/logger';
import { authClient, operatorClient, onboardingClient } from 'domain/HttpClients';
import { morphFormAddressToRequestPayload } from 'domain/batch/sheets/utils';
import moment from 'moment';
import uuid from 'uuid/v4';
import { FORM_NAME } from 'domain/operations/drivers/create/constants';
import { show as showNotification } from 'circe/notifications/actions';
import * as I18n from 'circe/I18n';
import { confirmCreation } from 'domain/operations/drivers/create/actions';

function* doCreate(context, action) {
  if (action.payload.name !== FORM_NAME) return;
  yield put(startSubmit(FORM_NAME));
  try {
    const {
      payload: { values },
    } = action;
    const { personalInformation } = values;

    const currentLocalStorage = JSON.stringify(localStorage);
    let tokens;
    let profileId;
    let accountId;

    try {
      const refId = yield getPreferredUsername({ ...personalInformation });
      const credentials = yield signupFunction(values, refId);
      tokens = yield loginFunction(credentials);
    } catch (e) {
      console.error(e);
      throw new Error(e.message);
    } finally {
      localStorage.clear();
      const data = JSON.parse(currentLocalStorage);
      Object.keys(data).forEach((k) => {
        localStorage.setItem(k, data[k]);
      });
    }
    try {
      const profileData = yield createProfile(personalInformation, tokens);
      profileId = profileData.profileId;
      accountId = profileData.accountId;
    } catch (e) {
      console.error(e);
      throw new Error(e.message);
    }
    yield registerDriverInformation(profileId, accountId, tokens.idToken, values);

    yield activateProfile(profileId);
    yield put(reset(FORM_NAME));
    yield put(
      showNotification(
        I18n.str('driverCreatedSuccessfully', 'Driver Created Successfully.'),
        'success',
      ),
    );
    setTimeout(() => {
      context.history.push('/drivers');
    }, 5000);
  } catch (e) {
    exception(e);
    console.error(e);
  } finally {
    yield put(stopSubmit(FORM_NAME));
  }
}

function* activateProfile(profileId) {
  try {
    const resp = yield operatorClient.Patch(`/api/profiles/${profileId}/activate`).send();
    return resp;
  } catch (e) {
    throw new Error(e.message);
  }
}
function* createProfile(personalInformation, tokens) {
  const headers = {
    Authorization: `Bearer ${tokens.idToken}`,
  };

  const {
    firstName,
    lastName,
    phone,
    address = 'Address',
    email = '',
    token,
    referenceId,
    idNumber,
    issuingNumber,
  } = personalInformation;
  const data = {
    firstName,
    lastName,
    phone,
    address,
    email,
    idInfo: {
      id: idNumber,
      issuingNumber,
    },
  };

  try {
    const self = yield authClient
      .Post('/api/self')
      .withHeaders(headers)
      .withData(data)
      .send();

    const params = {
      refId: referenceId,
      role: 'DRIVER',
      token: encodeURIComponent(token || DOMAIN_TOKEN),
      domain: SYSTEM_DOMAIN,
    };
    const profile = yield authClient
      .Post('/api/self/profiles')
      .withHeaders(headers)
      .withParams(params)
      .send();
    return profile;
  } catch (e) {
    throw new Error(e.message);
  }
}

function* getPreferredUsername(user) {
  const {
    phone,
    email = '',
    referenceId,
    firstName,
    lastName,
    token,
    idNumber,
    issuingNumber,
  } = user;

  const data = {
    companyCode: token || DOMAIN_TOKEN,
    domain: SYSTEM_DOMAIN,
    profileRole: 'DRIVER',
    userInfo: {
      firstName,
      lastName,
      phone,
      id: idNumber,
      issuingNumber,
    },
    username: phone,
  };
  try {
    const response = yield authClient
      .Post('/public-api/user-admin/get-preferred-username')
      .withData(data)
      .send();

    return response;
  } catch (e) {
    throw new Error(e.message);
  }
}

function* signupFunction(values, preferredUsername) {
  const { personalInformation, vehicleDocuments = {}, truckDetails = {} } = values;
  const {
    phone,
    email = '',
    referenceId,
    firstName,
    lastName,
    token,
    password,
    baseLocation,
    idNumber,
    issuingNumber,
  } = personalInformation;

  const { country, city } = morphFormAddressToRequestPayload(baseLocation);
  const data = {
    clientId: AWS_USER_POOL_WEB_CLIENT_ID,
    customInfo: {
      companyCode: token || DOMAIN_TOKEN,
      defaultCountry: country,
      isIndividual: '1',
      refId: referenceId,
    },
    domain: SYSTEM_DOMAIN,
    password,
    userInfo: {
      firstName,
      lastName,
      phone,
      email,
      address: city,
      preferredUsername,
      id: idNumber,
      issuingNumber,
    },
    username: phone,
  };
  try {
    const response = yield authClient
      .Post('/public-api/user-admin/signup')
      .withData(data)
      .send();

    return {
      phone,
      password,
    };
  } catch (e) {
    if (
      e.response
      && e.response.duplicated
      && !e.response.success
      && !e.response.userId
      && e.response.duplicatedUsers.length
    ) {
      yield put(showNotification(I18n.str('duplicatedDriver', 'Duplicated Driver.'), 'error'));
      yield put(confirmCreation(e.response.duplicatedUsers[0]));
    }
    console.error(e);
    throw new Error(e);
  }
}

function* loginFunction({ phone, password }) {
  try {
    const data = {
      clientId: AWS_USER_POOL_WEB_CLIENT_ID,
      domain: SYSTEM_DOMAIN,
      password,
      username: phone,
    };
    const resp = yield authClient
      .Post('/public-api/user-admin/signin')
      .withData(data)
      .send();

    const tokens = {
      accessToken: resp.accessToken,
      refreshToken: resp.refreshToken,
      idToken: resp.idToken,
    };

    return tokens;
  } catch (e) {
    throw new Error(e.message);
  }
}

function* registerDriverInformation(profileId, accountId, token, values) {
  const {
    truckDetails,
    personalInformation: { baseLocation, within: maxDistance },
  } = values;
  const { coordinate, city } = morphFormAddressToRequestPayload(baseLocation);
  const { latitude, longitude } = coordinate;
  let vehicleInfo = null;
  let licenseInfo = null;
  if (truckDetails !== null && truckDetails !== undefined && Object.keys(truckDetails).length) {
    const {
      truckType: type,
      capacity,
      license,
      length: capacityLenght,
      width: capacityWidth,
      height: capacityHeight,
      maxWeight,
      fleetId,
      details,
      equipmentId,
      ownerShipType,
    } = truckDetails;
    const {
      maxTrailers, axles, label, startingWeight, tareWeight, size,
    } = details;

    const { length, width, height } = size;
    licenseInfo = license;
    vehicleInfo = {
      baseLocation: {
        latitude,
        longitude,
      },
      homeCity: city,
      fleetId,
      maxDistance,
      params: {
        address: null,
        axles,
        vehicleOwnershipType: ownerShipType,
        capacity: {
          height: capacityHeight,
          length: capacityLenght,
          width: capacityWidth,
        },
        licensePlate: license,
        maxTrailers,
        maxWeight,
        name: equipmentId || label,
        owner: accountId,
        size: {
          length,
          width,
          height,
        },
        startingWeight,
        tareWeight,
        tempRange: {
          excludeMax: true,
          excludeMin: true,
          maximum: 0,
          minimum: 0,
        },
        type,
        vin: null,
      },
      vehicleId: null,
    };
  }

  const data = {
    driverInfo: {
      homeCity: city,
      base: {
        latitude,
        longitude,
      },
      license: licenseInfo,
      maxDistance,
      tags: [],
    },
    vehicleInfo,
  };
  try {
    const response = yield onboardingClient
      .Post(`/api/drivers/${profileId}/register/v2`)
      .withHeaders({ Authorization: `Bearer ${token}`, 'Content-type': 'application/json' })
      .withData(data)
      .send();
  } catch (e) {
    console.error(e);
    throw new Error(e.message);
  }
}

function* watcher(context) {
  yield all([takeEvery(doSubmit().type, doCreate, context)]);
}

export default watcher;
